import { graphqlRequest } from "@/graphql/client";
import { createPropertyShortLink as createPropertyShortLinkMutation } from "@/graphql/mutations";
import { sanitizeCalculationMedia } from "@/utils/maoCalculator/sanitizeCalculationMedia";

/**
 * Local `putMaoc` operation that requests the backend-built share link via
 * `return_url: true`. We define it inline (rather than importing the codegen
 * `putMaoc`) so this share-flow stays decoupled from regenerated operations
 * and so we can pass the `return_url` variable required by the Lambda.
 */
const putMaocWithReturnUrl = /* GraphQL */ `
  query PutMaocShare(
    $customer: String!
    $buyer: String!
    $seller: String!
    $status: String!
    $property: String!
    $property_customer: String
    $calculation: AWSJSON!
    $county: String!
    $editor: String!
    $metadata: AWSJSON
    $return_url: Boolean
  ) {
    putMaoc(
      customer: $customer
      buyer: $buyer
      seller: $seller
      status: $status
      property: $property
      property_customer: $property_customer
      calculation: $calculation
      county: $county
      editor: $editor
      metadata: $metadata
      return_url: $return_url
    ) {
      statusCode
      body
    }
  }
`;

/** Session cache TTL for short URLs (aligned to typical share-session lifetime). */
const PROPERTY_SHORT_LINK_CACHE_TTL_MS = 30 * 60 * 1000;
/** Bound hung Lambda/AppSync warm attempts so Share Link can retry. */
const PROPERTY_SHORT_LINK_WARM_TIMEOUT_MS = 15_000;
/** After a permanent warm failure, skip immediate re-create storms. */
const PROPERTY_SHORT_LINK_FAILURE_COOLDOWN_MS = 5_000;

/**
 * Hostnames allowed for clipboard/share short links (protocol check alone is insufficient).
 * Server remains source of truth; this is defense in depth only.
 */
function isAllowedShareLinkHostname(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  if (host === "localhost" || host === "127.0.0.1") return true;
  return host === "horaizen.io" || host.endsWith(".horaizen.io");
}

/**
 * @param {unknown} statusCode
 * @returns {boolean}
 */
function isHttpSuccessStatusCode(statusCode) {
  const n = Number(statusCode);
  return Number.isFinite(n) && n >= 200 && n < 300;
}

export const SHARED_PROPERTY_TOP_LEVEL_KEYS = [
  "full_address",
  "city",
  "county",
  "state",
  "zipcode",
  "hidden_address",
  "fit_score",
];

export const SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS = [
  "listing_price",
  "bedrooms",
  "bathrooms",
  "square_feet",
  "property_type",
  "arv_value",
  "estimated_price_low",
  "estimated_price_high",
  "estimated_price_avg",
  "qma_s3_key",
  "qma_pdf_s3_key",
  "pdf_s3_key",
  "comps_analysis",
  "comps_html_s3_key",
  "comps_pdf_s3_key",
  "sow_s3_key",
  "sow_pdf_path",
  "sow_estimate_report",
  "sow_json_data",
];

function pickFields(source, keys) {
  if (!source || typeof source !== "object") return {};
  const out = {};
  for (const key of keys) {
    if (source[key] !== undefined) {
      out[key] = source[key];
    }
  }
  return out;
}

function mergeDefinedFields(target, source, keys) {
  if (!source || typeof source !== "object") return;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      target[key] = value;
    }
  }
}

/**
 * Overlays calculation-embedded share fields onto a MAOC snapshot property.
 * Needed when getMaoc returns `property` without report artifacts that live only
 * on the flattened calculation payload (comps/QMA/SOW keys).
 *
 * @param {object|null|undefined} snapshotProperty
 * @param {object|null|undefined} sharedProperty
 * @returns {object|null}
 */
export function mergeSharedPropertySnapshotFields(snapshotProperty, sharedProperty) {
  if (!sharedProperty || typeof sharedProperty !== "object") {
    return snapshotProperty ?? null;
  }
  if (!snapshotProperty || typeof snapshotProperty !== "object") {
    return { ...sharedProperty };
  }

  const merged = {
    ...snapshotProperty,
    additional_attributes: {
      ...(snapshotProperty.additional_attributes && typeof snapshotProperty.additional_attributes === "object"
        ? snapshotProperty.additional_attributes
        : {}),
    },
  };

  mergeDefinedFields(merged, sharedProperty, SHARED_PROPERTY_TOP_LEVEL_KEYS);
  mergeDefinedFields(merged.additional_attributes, sharedProperty, SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS);
  mergeDefinedFields(merged.additional_attributes, sharedProperty.additional_attributes, SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS);

  if (merged.hidden_address) {
    delete merged.full_address;
  }

  return merged;
}

/**
 * Collects the flat share fields (property descriptor + report artifact keys)
 * used to unify the share-link and distribute
 * payloads. Spread the result onto a `calculation` object before persisting.
 *
 * @param {object} property
 * @param {object} [extraAdditionalAttrs] - Extra values to merge into share fields.
 * @returns {object|null}
 */
export function buildSharedCalculationFields(property, extraAdditionalAttrs) {
  if (!property || typeof property !== "object") return null;

  const sharedFields = pickFields(property, SHARED_PROPERTY_TOP_LEVEL_KEYS);
  if (sharedFields.hidden_address) {
    delete sharedFields.full_address;
  }

  // Prefer additional_attributes values when present, but also support legacy
  // top-level report fields on property for backward compatibility.
  mergeDefinedFields(sharedFields, property, SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS);
  mergeDefinedFields(sharedFields, property.additional_attributes, SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS);

  if (extraAdditionalAttrs && typeof extraAdditionalAttrs === "object") {
    mergeDefinedFields(sharedFields, extraAdditionalAttrs, SHARED_PROPERTY_ADDITIONAL_ATTR_KEYS);
  }

  // Backend schema requires numeric types for these fields.
  const NUMERIC_KEYS = [
    "listing_price", "bedrooms", "bathrooms", "square_feet",
    "arv_value", "estimated_price_low", "estimated_price_high", "estimated_price_avg",
  ];
  for (const key of NUMERIC_KEYS) {
    if (key in sharedFields && typeof sharedFields[key] !== "number") {
      const n = Number(sharedFields[key]);
      if (Number.isFinite(n)) {
        sharedFields[key] = n;
      } else {
        delete sharedFields[key];
      }
    }
  }

  return Object.keys(sharedFields).length > 0 ? sharedFields : null;
}

/**
 * Creates a shareable MAOC snapshot and returns the backend-built share link.
 *
 * The backend Lambda (`dev-cai-web-save-maoc`) receives `return_url: true` and
 * responds with `{ link: "<encrypted share url>" }` in its body. The link is
 * fully constructed server-side so no client-side encryption is involved.
 *
 * @param {object} params
 * @param {string} params.customerId
 * @param {string} params.buyerId
 * @param {string} params.sellerId - Email of the logged-in user creating the share.
 * @param {string} params.propertyId
 * @param {string} [params.propertyCustomerId]
 * @param {object} params.calculation
 * @param {object} [params.property] - Property to embed in metadata for the public viewer.
 * @param {object} [params.metadata]
 * @param {object} [params.sowJsonData]
 * @param {object} [params.compsExtraFields]
 * @returns {Promise<{ link: string }>}
 */
export async function createPublicMaocShareSnapshot({
  customerId,
  buyerId,
  sellerId,
  propertyId,
  propertyCustomerId,
  calculation,
  property,
  metadata = {},
  sowJsonData = null,
  compsExtraFields = null,
}) {
  const normalizedSowJsonData =
    sowJsonData && typeof sowJsonData === "object" ? sowJsonData : undefined;
  const normalizedCompsExtraFields =
    compsExtraFields && typeof compsExtraFields === "object" ? compsExtraFields : undefined;
  const sharedCalculationFields = buildSharedCalculationFields(property, {
    ...(normalizedCompsExtraFields || {}),
    sow_json_data: normalizedSowJsonData,
  });
  const finalMetadata = { ...metadata };
  const calculationWithSnapshot = sanitizeCalculationMedia(
    sharedCalculationFields ? { ...calculation, ...sharedCalculationFields } : calculation
  );

  const response = await graphqlRequest({
    query: putMaocWithReturnUrl,
    variables: {
      customer: customerId,
      property_customer: propertyCustomerId || customerId,
      buyer: buyerId,
      seller: sellerId || "",
      status: "OPEN",
      property: propertyId,
      calculation: JSON.stringify(calculationWithSnapshot ?? {}),
      county: property?.county || "",
      editor: sellerId || buyerId,
      metadata: JSON.stringify(finalMetadata),
      return_url: true,
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResponse = response?.data?.putMaoc;
  if (!apiResponse?.body) {
    throw new Error("Unable to create share snapshot.");
  }

  let bodyData = null;
  try {
    bodyData = typeof apiResponse.body === "string" ? JSON.parse(apiResponse.body) : apiResponse.body;
  } catch {
    throw new Error("Invalid share snapshot response.");
  }

  if (!isHttpSuccessStatusCode(apiResponse.statusCode)) {
    const message =
      typeof bodyData?.error === "string" && bodyData.error.trim()
        ? bodyData.error.trim()
        : typeof bodyData?.message === "string" && bodyData.message.trim()
          ? bodyData.message.trim()
          : "Unable to create share snapshot.";
    if (import.meta.env.DEV) {
      console.warn("[ShareLink] putMaoc return_url non-2xx", {
        statusCode: apiResponse.statusCode,
        error: bodyData?.error,
      });
    }
    throw new Error(message);
  }

  if (bodyData?.error) {
    throw new Error(
      typeof bodyData.error === "string" && bodyData.error.trim()
        ? bodyData.error.trim()
        : "Unable to create share snapshot."
    );
  }

  const link = typeof bodyData?.link === "string" ? bodyData.link.trim() : "";
  if (!link) {
    throw new Error("Share link is missing from response.");
  }

  assertSafeShareLinkUrl(link);

  return { link };
}

/**
 * The Lambda builds the link, but the client must not copy dangerous schemes into the clipboard.
 * Full authz and URL policy remain server-side; this only blocks e.g. `javascript:` / `data:` if a response is malformed.
 * @param {string} link
 * @returns {URL}
 */
function assertSafeShareLinkUrl(link) {
  let parsed;
  try {
    parsed = new URL(link);
  } catch {
    throw new Error("Share link is not a valid URL.");
  }
  if (parsed.protocol === "https:") return parsed;
  if (import.meta.env.DEV && parsed.protocol === "http:") return parsed;
  throw new Error("Share link must use a safe http(s) URL.");
}

/**
 * Property short links also require an allowlisted host (protocol-only is insufficient).
 * @param {string} link
 */
function assertSafePropertyShortLinkUrl(link) {
  const parsed = assertSafeShareLinkUrl(link);
  if (!isAllowedShareLinkHostname(parsed.hostname)) {
    throw new Error("Share link host is not allowed.");
  }
}

/**
 * Creates a short property share link via AppSync (Cognito user pools).
 * Server builds the allowlisted `/property/{id}` target from `propertyId`.
 * ASSUMPTION: schema/Lambda use camelCase `propertyId` (unlike neighboring `property_id` args).
 *
 * @param {object} params
 * @param {string} params.propertyId - Store property id (may include `property#`; server strips).
 * @param {string} params.customer
 * @returns {Promise<{ shortUrl: string }>}
 */
export async function createPropertyShortLink({ propertyId, customer }) {
  const normalizedPropertyId = typeof propertyId === "string" ? propertyId.trim() : "";
  const normalizedCustomer = typeof customer === "string" ? customer.trim() : "";
  if (!normalizedPropertyId || !normalizedCustomer) {
    throw new Error("propertyId and customer are required to create a short property link.");
  }

  const response = await graphqlRequest({
    query: createPropertyShortLinkMutation,
    variables: {
      propertyId: normalizedPropertyId,
      customer: normalizedCustomer,
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResponse = response?.data?.createPropertyShortLink;
  if (!apiResponse?.body) {
    throw new Error("Unable to create property short link.");
  }

  if (!isHttpSuccessStatusCode(apiResponse.statusCode)) {
    if (import.meta.env.DEV) {
      console.warn("[ShareLink] createPropertyShortLink non-2xx", {
        statusCode: apiResponse.statusCode,
      });
    }
    throw new Error("Unable to create property short link.");
  }

  let bodyData = null;
  try {
    bodyData = typeof apiResponse.body === "string" ? JSON.parse(apiResponse.body) : apiResponse.body;
  } catch {
    throw new Error("Invalid createPropertyShortLink response.");
  }

  if (bodyData?.error) {
    if (import.meta.env.DEV) {
      console.warn("[ShareLink] createPropertyShortLink body.error", bodyData.error);
    }
    throw new Error("Unable to create property short link.");
  }

  // Lambda contract: snake_case body keys only.
  const shortUrl = typeof bodyData?.short_url === "string" ? bodyData.short_url.trim() : "";
  if (!shortUrl) {
    throw new Error("Short URL is missing from createPropertyShortLink response.");
  }

  assertSafePropertyShortLinkUrl(shortUrl);

  return { shortUrl };
}

/** @typedef {{ url: string, expiresAt: number }} PropertyShortLinkCacheEntry */

/** @type {Map<string, PropertyShortLinkCacheEntry>} */
const propertyShortLinkCache = new Map();
/** @type {Map<string, Promise<string|null>>} */
const propertyShortLinkWarmInFlight = new Map();
/** @type {Map<string, number>} epoch ms until which warm retries are suppressed after failure */
const propertyShortLinkFailureUntil = new Map();

/**
 * @param {string} propertyId
 * @param {string} customer
 * @returns {string|null}
 */
function propertyShortLinkCacheKey(propertyId, customer) {
  const normalizedPropertyId = typeof propertyId === "string" ? propertyId.trim() : "";
  const normalizedCustomer = typeof customer === "string" ? customer.trim() : "";
  if (!normalizedPropertyId || !normalizedCustomer) return null;
  return `${normalizedPropertyId}\0${normalizedCustomer}`;
}

/**
 * @param {string} key
 * @returns {string|null}
 */
function readFreshCachedShortUrl(key) {
  const entry = propertyShortLinkCache.get(key);
  if (!entry || typeof entry.url !== "string" || entry.url.length === 0) return null;
  if (Date.now() >= entry.expiresAt) {
    propertyShortLinkCache.delete(key);
    return null;
  }
  return entry.url;
}

/**
 * Sync short URL for the Share Link click path. Returns null until warm succeeds.
 * Never returns the long `/property/{id}` URL and never awaits network.
 *
 * @param {object} params
 * @param {string} params.propertyId
 * @param {string} params.customer
 * @returns {string|null}
 */
export function getPropertyShareLinkForCopy({ propertyId, customer }) {
  const key = propertyShortLinkCacheKey(propertyId, customer);
  if (!key) return null;
  return readFreshCachedShortUrl(key);
}

/**
 * True while a prior warm failed and cooldown has not elapsed (distinct from "still preparing").
 *
 * @param {object} params
 * @param {string} params.propertyId
 * @param {string} params.customer
 * @returns {boolean}
 */
export function hasPropertyShareLinkWarmFailed({ propertyId, customer }) {
  const key = propertyShortLinkCacheKey(propertyId, customer);
  if (!key) return false;
  const until = propertyShortLinkFailureUntil.get(key);
  return typeof until === "number" && until > Date.now();
}

/**
 * Background short-link create for the next copy. Safe to fire-and-forget from hover/focus/click.
 *
 * @param {object} params
 * @param {string} params.propertyId
 * @param {string} params.customer
 * @returns {Promise<string|null>} short URL when created, else null
 */
export function warmPropertyShareLink({ propertyId, customer }) {
  const normalizedPropertyId = typeof propertyId === "string" ? propertyId.trim() : "";
  const normalizedCustomer = typeof customer === "string" ? customer.trim() : "";
  const key = propertyShortLinkCacheKey(normalizedPropertyId, normalizedCustomer);
  if (!key) return Promise.resolve(null);

  const cached = readFreshCachedShortUrl(key);
  if (cached) return Promise.resolve(cached);

  if (hasPropertyShareLinkWarmFailed({ propertyId: normalizedPropertyId, customer: normalizedCustomer })) {
    return Promise.resolve(null);
  }

  const inFlight = propertyShortLinkWarmInFlight.get(key);
  if (inFlight) return inFlight;

  const warmPromise = (async () => {
    let timeoutId;
    try {
      const { shortUrl } = await Promise.race([
        createPropertyShortLink({
          propertyId: normalizedPropertyId,
          customer: normalizedCustomer,
        }),
        new Promise((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error("Share link create timed out."));
          }, PROPERTY_SHORT_LINK_WARM_TIMEOUT_MS);
        }),
      ]);
      if (typeof shortUrl === "string" && shortUrl.length > 0) {
        propertyShortLinkCache.set(key, {
          url: shortUrl,
          expiresAt: Date.now() + PROPERTY_SHORT_LINK_CACHE_TTL_MS,
        });
        propertyShortLinkFailureUntil.delete(key);
        return shortUrl;
      }
      propertyShortLinkFailureUntil.set(key, Date.now() + PROPERTY_SHORT_LINK_FAILURE_COOLDOWN_MS);
      return null;
    } catch (error) {
      propertyShortLinkFailureUntil.set(key, Date.now() + PROPERTY_SHORT_LINK_FAILURE_COOLDOWN_MS);
      if (import.meta.env.DEV) {
        console.warn("[ShareLink] createPropertyShortLink failed", error);
      }
      return null;
    } finally {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      propertyShortLinkWarmInFlight.delete(key);
    }
  })();

  propertyShortLinkWarmInFlight.set(key, warmPromise);
  return warmPromise;
}

/**
 * Clears in-memory short-link cache (logout, customer switch, tests).
 */
export function clearPropertyShareLinkCache() {
  propertyShortLinkCache.clear();
  propertyShortLinkWarmInFlight.clear();
  propertyShortLinkFailureUntil.clear();
}
